import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import { atom, useAtom } from 'jotai';
import {
    MODERATION_REASONS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.moderation.reasons';
import {
    REPORT_USER_API_ROUTE,
    type action,
} from '~/routes/remix-api.moderation.report-user';
import type { GetModerationReasonsQuery } from 'generated/graphql';
import { ModerationEntityTypeEnum } from 'generated/graphql';

const moderationReasonsAtom = atom<
    GetModerationReasonsQuery['moderationReasons']
>([]);

/**
 * Hook to manage user moderation. Gets the list of moderation reasons and has
 * a callback to report a user.
 */
export function useUserModeration({
    onReportSuccess,
    onReportError,
}: {
    onReportSuccess?: () => void;
    onReportError?: (message: string) => void;
} = {}) {
    const [moderationReasons, setModerationReasons] = useAtom(
        moderationReasonsAtom
    );
    const isFetched = useRef(false);
    const fetcher = useFetcher<typeof loader>({ key: 'user-moderation' });
    const reportFetcher = useFetcher<typeof action>();
    const latestOnReportSuccess = useLatest(onReportSuccess);
    const latestOnReportError = useLatest(onReportError);
    const apiUrl = useMemo(() => {
        const url = new URL(
            MODERATION_REASONS_API_ROUTE,
            window.location.origin
        );
        url.searchParams.set('entityType', ModerationEntityTypeEnum.User);
        return `${url.pathname}${url.search}`;
    }, []);

    const reportUser = useCallback(
        (
            userID: number,
            moderationReasonID: string | number,
            note: string,
            metadata?: Record<string, string | number | boolean | null>
        ) => {
            reportFetcher.submit(
                {
                    userID,
                    moderationReasonID,
                    note,
                    metadata: metadata ?? null,
                },
                {
                    action: REPORT_USER_API_ROUTE,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
        },
        [reportFetcher]
    );

    useEffect(() => {
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            // Fetch initial moderation reasons on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        }
    }, [fetcher, apiUrl]);

    // Set data from fetcher and cache results.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const reasons = fetcher.data.moderationReasons
            // Exclude child reasons from the top level reasons.
            .filter((reason) => !reason.ParentID)
            .map((reason) => ({
                ...reason,
                // Only include children with the User entity type.
                Children: reason.Children.filter((child) => {
                    return child.EntityTypes.includes(
                        ModerationEntityTypeEnum.User
                    );
                }),
            }));
        setModerationReasons(reasons);
    }, [fetcher, setModerationReasons]);

    // Run callback when report user mutation finishes.
    useEffect(() => {
        if (reportFetcher.data && reportFetcher.state === 'loading') {
            const response = reportFetcher.data.response;
            if (response.__typename === 'ModerationUserReportCreated') {
                latestOnReportSuccess.current?.();
            } else {
                latestOnReportError.current?.(response.Message);
            }
        }
    }, [reportFetcher, latestOnReportSuccess, latestOnReportError]);

    return {
        /**
         * The list of moderation reasons.
         */
        moderationReasons,
        /**
         * The moderation reasons are loading.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The report user fetcher is submitting.
         */
        isSubmitting: reportFetcher.state === 'submitting',
        /**
         * Report a user for moderation.
         */
        reportUser,
    };
}
